import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Building2, DollarSign, TrendingDown, Users, Loader2 } from "lucide-react";
import { TID } from "@/lib/testIds";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-6 rounded-xl border-border bg-card animate-fade-up">
      <div className="flex items-start justify-between mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div data-testid={testid} className="font-display font-semibold tracking-tight text-3xl sm:text-4xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
}

export default function MasterDashboard() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: a }] = await Promise.all([
          api.get("/master/stats"),
          api.get("/master/audit", { params: { limit: 8 } }),
        ]);
        setData(s);
        setAudit(a.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Painel da Matriz</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Visão Geral</h1>
        <p className="text-sm text-muted-foreground mt-1">Saúde da plataforma, empresas e operação geral.</p>
      </div>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard testid={TID.masterStatsMrr} icon={DollarSign} label="Receita Mensal Recorrente" value={`R$ ${(data.mrr || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} sub="Soma das mensalidades ativas (MRR)" />
            <StatCard testid={TID.masterStatsTenants} icon={Building2} label="Empresas" value={data.tenants_total} sub={`${data.tenants_active} ativas · ${data.tenants_suspended} suspensas`} />
            <StatCard testid={TID.masterStatsChurn} icon={TrendingDown} label="Cancelamentos" value={`${data.churn_rate}%`} sub="Empresas suspensas sobre o total" />
            <StatCard testid={TID.masterStatsUsers} icon={Users} label="Usuários" value={data.users_total} sub="Equipe da matriz + todas as empresas" />
          </div>

          <Card className="rounded-xl border-border bg-card">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-display text-xl font-semibold">Atividade Recente</div>
                <div className="text-xs text-muted-foreground mt-1">Histórico geral de alterações</div>
              </div>
            </div>
            <ul className="divide-y divide-border">
              {audit.length === 0 ? (
                <li className="px-6 py-12 text-center text-sm text-muted-foreground">Nenhuma atividade ainda.</li>
              ) : audit.map((a) => (
                <li key={a.id} className="px-6 py-3.5 flex items-center justify-between gap-4 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.action}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {a.actor_email || "—"} · {a.resource}{a.resource_id ? `:${a.resource_id.slice(0, 8)}` : ""}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString("pt-BR")}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}
